import React, { useState, useEffect } from 'react';
import { Building2, Lock, User, LogIn, AlertCircle, Users, Shield, FileText } from 'lucide-react';
import { TeacherAbsenty } from './TeacherAbsenty';

export const SchoolPage: React.FC = () => {
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeView, setActiveView] = useState<'dashboard' | 'teacher' | 'nightguard' | 'clerk'>('dashboard');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeView, isLoggedIn]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginId === '10151002905' && password === 'Mk@2905') {
      setIsLoggedIn(true);
      setError('');
    } else {
      setError('Invalid Login ID or Password');
    }
  };

  if (isLoggedIn) {
    if (activeView === 'teacher') {
      return <TeacherAbsenty onBack={() => setActiveView('dashboard')} />;
    }

    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6 animate-fadeIn">
        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl p-5 md:p-8 border border-gray-100">
          <div className="flex flex-col sm:flex-row items-center justify-between mb-8 md:mb-12 space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3 md:space-x-4">
              <div className="bg-emerald-100 p-3 md:p-4 rounded-xl md:rounded-2xl text-emerald-600">
                <Building2 size={24} className="md:hidden" />
                <Building2 size={32} className="hidden md:block" />
              </div>
              <div>
                <h2 className="text-xl md:text-3xl font-black text-gray-800 tracking-tight">School Dashboard</h2>
                <p className="text-xs md:text-sm text-gray-500 font-medium">Welcome back, {loginId}</p>
              </div>
            </div>
            <button 
              onClick={() => {
                setIsLoggedIn(false);
                setLoginId('');
                setPassword('');
                setActiveView('dashboard');
              }}
              className="w-full sm:w-auto px-5 md:px-6 py-2.5 md:py-3 bg-gray-100 hover:bg-red-50 text-gray-700 hover:text-red-600 font-bold rounded-xl transition-colors flex items-center justify-center space-x-2 text-sm md:text-base"
            >
              <span>Log Out</span>
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            <button 
              onClick={() => setActiveView('teacher')}
              className="bg-white border-2 border-emerald-100 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1 transition-all rounded-2xl md:rounded-[2rem] p-6 md:p-8 text-left group"
            >
              <div className="bg-emerald-50 w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-emerald-500 transition-colors">
                <Users size={24} className="md:hidden text-emerald-600 group-hover:text-white transition-colors" />
                <Users size={32} className="hidden md:block text-emerald-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg md:text-2xl font-black text-gray-800 mb-1 md:mb-2">TEACHER ABSENTY</h3>
              <p className="text-xs md:text-sm text-gray-500 font-medium">Manage attendance and details for teaching staff</p>
            </button>

            <button 
              onClick={() => setActiveView('nightguard')}
              className="bg-white border-2 border-blue-100 hover:border-blue-500 hover:shadow-xl hover:-translate-y-1 transition-all rounded-2xl md:rounded-[2rem] p-6 md:p-8 text-left group"
            >
              <div className="bg-blue-50 w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-blue-500 transition-colors">
                <Shield size={24} className="md:hidden text-blue-600 group-hover:text-white transition-colors" />
                <Shield size={32} className="hidden md:block text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg md:text-2xl font-black text-gray-800 mb-1 md:mb-2">NIGHT GUARD ABSENTY</h3>
              <p className="text-xs md:text-sm text-gray-500 font-medium">Manage attendance for security personnel</p>
            </button>

            <button 
              onClick={() => setActiveView('clerk')}
              className="bg-white border-2 border-purple-100 hover:border-purple-500 hover:shadow-xl hover:-translate-y-1 transition-all rounded-2xl md:rounded-[2rem] p-6 md:p-8 text-left group"
            >
              <div className="bg-purple-50 w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:bg-purple-500 transition-colors">
                <FileText size={24} className="md:hidden text-purple-600 group-hover:text-white transition-colors" />
                <FileText size={32} className="hidden md:block text-purple-600 group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg md:text-2xl font-black text-gray-800 mb-1 md:mb-2">CLERK ABSENTY</h3>
              <p className="text-xs md:text-sm text-gray-500 font-medium">Manage attendance for administrative staff</p>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto p-6 pt-12 animate-fadeIn">
      <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 border border-gray-100 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-br from-orange-500 to-red-600 opacity-10 rounded-b-[3rem]"></div>
        
        <div className="relative z-10">
          <div className="flex justify-center mb-8">
            <div className="bg-gradient-to-br from-orange-500 to-red-600 p-5 rounded-3xl text-white shadow-lg shadow-orange-200">
              <Building2 size={40} />
            </div>
          </div>
          
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-gray-800 tracking-tight mb-2">School Login</h2>
            <p className="text-gray-500 font-medium">Enter your credentials to access the portal</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start space-x-3 text-red-600 animate-shake">
              <AlertCircle size={20} className="shrink-0 mt-0.5" />
              <p className="text-sm font-bold">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Login ID</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <User size={20} />
                </div>
                <input
                  type="text"
                  value={loginId}
                  onChange={(e) => setLoginId(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:bg-white focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 rounded-2xl font-bold text-gray-800 outline-none transition-all"
                  placeholder="Enter Login ID"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Lock size={20} />
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent focus:bg-white focus:border-orange-500 focus:ring-4 focus:ring-orange-500/10 rounded-2xl font-bold text-gray-800 outline-none transition-all"
                  placeholder="Enter Password"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="w-full py-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-2xl font-black text-lg shadow-lg shadow-orange-200/50 flex items-center justify-center space-x-2 transition-all hover:-translate-y-1"
            >
              <span>Login</span>
              <LogIn size={20} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
